import { InteractionRequiredAuthError } from '@azure/msal-browser';
import {
  API_BASE_PATH,
  IMPERSONATE_MEMBER_HEADER,
  USER_ACTIVITY_HEADER,
  type ApiErrorEnvelope,
} from '@catalyst-psa/shared';

import { getImpersonatedMemberId } from '../auth/impersonation.js';
import { apiTokenRequest, msalInstance } from '../auth/msalConfig.js';
import { env } from '../config/env.js';

/**
 * Presence: remember when the user last genuinely interacted with the page, so requests
 * can tell the backend "a human is here" — background pollers from idle tabs then no
 * longer keep a member flagged online.
 */
const ACTIVITY_WINDOW_MS = 2 * 60_000;
let lastInteractionAt = Date.now();

if (typeof window !== 'undefined') {
  const markActive = (): void => {
    lastInteractionAt = Date.now();
  };
  for (const event of ['pointerdown', 'keydown', 'wheel', 'touchstart'] as const) {
    window.addEventListener(event, markActive, { capture: true, passive: true });
  }
}

function isUserActive(): boolean {
  return Date.now() - lastInteractionAt < ACTIVITY_WINDOW_MS;
}

/** Error thrown for any non-2xx API response, carrying the parsed error envelope when present. */
export class ApiError extends Error {
  readonly status: number;
  readonly code: string | undefined;
  readonly envelope: ApiErrorEnvelope | undefined;

  constructor(message: string, status: number, code?: string, envelope?: ApiErrorEnvelope) {
    super(message);
    this.name = 'ApiError';
    this.status = status;
    this.code = code;
    this.envelope = envelope;
  }
}

/**
 * Renders a react-query mutation/query error as a message: the server text for an
 * {@link ApiError} (e.g. a permission failure), otherwise the caller's fallback. Returns
 * null when there is no error, so results can be chained with `??`.
 */
export function mutationError(error: unknown, fallback: string): string | null {
  if (error === null || error === undefined) return null;
  return error instanceof ApiError ? error.message : fallback;
}

async function getAccessToken(): Promise<string | null> {
  if (!msalInstance || apiTokenRequest.scopes.length === 0) {
    return null;
  }

  const account = msalInstance.getActiveAccount() ?? msalInstance.getAllAccounts()[0];
  if (!account) {
    return null;
  }

  try {
    const result = await msalInstance.acquireTokenSilent({ ...apiTokenRequest, account });
    return result.accessToken;
  } catch (error) {
    if (error instanceof InteractionRequiredAuthError) {
      await msalInstance.acquireTokenRedirect({ ...apiTokenRequest, account });
    }
    return null;
  }
}

export interface ApiRequestOptions extends Omit<RequestInit, 'body'> {
  /** JSON-serializable request body; serialized automatically unless a FormData instance. */
  body?: unknown;
}

async function parseError(response: Response): Promise<ApiError> {
  let envelope: ApiErrorEnvelope | undefined;
  let message = `Request failed with status ${response.status}`;

  try {
    const data = (await response.json()) as ApiErrorEnvelope | undefined;
    if (data && typeof data === 'object' && 'error' in data) {
      envelope = data;
      if (data.error?.message) {
        message = data.error.message;
      }
    }
  } catch {
    // Non-JSON error body; fall back to the status-based message.
  }

  return new ApiError(message, response.status, envelope?.error?.code, envelope);
}

/** Issue an authenticated request and return the raw Response (used for SSE streams). */
export async function apiFetchRaw(path: string, options: ApiRequestOptions = {}): Promise<Response> {
  const token = await getAccessToken();
  const headers = new Headers(options.headers);
  if (!headers.has('Accept')) headers.set('Accept', 'application/json');

  const isFormData = options.body instanceof FormData;
  if (options.body !== undefined && !isFormData) {
    headers.set('Content-Type', 'application/json');
  }
  if (token) {
    headers.set('Authorization', `Bearer ${token}`);
  }

  const impersonateMemberId = getImpersonatedMemberId();
  if (impersonateMemberId) {
    headers.set(IMPERSONATE_MEMBER_HEADER, impersonateMemberId);
  }

  if (isUserActive()) {
    headers.set(USER_ACTIVITY_HEADER, '1');
  }

  const body =
    options.body === undefined
      ? undefined
      : isFormData
        ? (options.body as FormData)
        : JSON.stringify(options.body);

  const response = await fetch(`${env.apiBaseUrl}${API_BASE_PATH}${path}`, {
    ...options,
    headers,
    body,
  });

  if (!response.ok) {
    throw await parseError(response);
  }
  return response;
}

/** Issue an authenticated request to the PSA API and parse the JSON response. */
export async function apiFetch<T>(path: string, options: ApiRequestOptions = {}): Promise<T> {
  const response = await apiFetchRaw(path, options);

  if (response.status === 204) {
    return undefined as T;
  }

  return (await response.json()) as T;
}

/** A downloaded binary response and its server-provided file name (when present). */
export interface DownloadResult {
  blob: Blob;
  fileName: string | null;
}

/** Parse a `filename="..."` value out of a Content-Disposition header. */
function parseFileName(disposition: string | null): string | null {
  if (!disposition) {
    return null;
  }
  const match = /filename\*?=(?:UTF-8'')?"?([^";]+)"?/i.exec(disposition);
  const value = match?.[1];
  return value ? decodeURIComponent(value) : null;
}

/** Issue an authenticated request that returns a binary body (e.g. a PDF), not JSON. */
export async function apiDownload(
  path: string,
  options?: { method?: 'GET' | 'POST'; body?: unknown },
): Promise<DownloadResult> {
  const token = await getAccessToken();
  const headers = new Headers();
  if (token) {
    headers.set('Authorization', `Bearer ${token}`);
  }

  const impersonateMemberId = getImpersonatedMemberId();
  if (impersonateMemberId) {
    headers.set(IMPERSONATE_MEMBER_HEADER, impersonateMemberId);
  }

  if (isUserActive()) {
    headers.set(USER_ACTIVITY_HEADER, '1');
  }

  const hasBody = options?.body !== undefined;
  if (hasBody) {
    headers.set('Content-Type', 'application/json');
  }

  const response = await fetch(`${env.apiBaseUrl}${API_BASE_PATH}${path}`, {
    method: options?.method ?? 'GET',
    headers,
    ...(hasBody ? { body: JSON.stringify(options?.body) } : {}),
  });

  if (!response.ok) {
    throw await parseError(response);
  }

  return {
    blob: await response.blob(),
    fileName: parseFileName(response.headers.get('Content-Disposition')),
  };
}

export const api = {
  get: <T>(path: string, options?: ApiRequestOptions): Promise<T> =>
    apiFetch<T>(path, { ...options, method: 'GET' }),
  download: (path: string): Promise<DownloadResult> => apiDownload(path),
  downloadPost: (path: string, body?: unknown): Promise<DownloadResult> =>
    apiDownload(path, { method: 'POST', body }),
  post: <T>(path: string, body?: unknown, options?: ApiRequestOptions): Promise<T> =>
    apiFetch<T>(path, { ...options, method: 'POST', body }),
  patch: <T>(path: string, body?: unknown, options?: ApiRequestOptions): Promise<T> =>
    apiFetch<T>(path, { ...options, method: 'PATCH', body }),
  put: <T>(path: string, body?: unknown, options?: ApiRequestOptions): Promise<T> =>
    apiFetch<T>(path, { ...options, method: 'PUT', body }),
  delete: <T = void>(path: string, options?: ApiRequestOptions): Promise<T> =>
    apiFetch<T>(path, { ...options, method: 'DELETE' }),
};
